import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Mail, Phone, MapPin, Building2 } from "lucide-react";

const STATIC = {
  about: { title: "About DailyBeans", body: "We started DailyBeans with a simple ambition: to bring the freshness of estate-roasted South Indian coffee to your doorstep. Every bag we ship is roasted in small batches, in the week you order it. From the misty hills of Chikmagalur to your kitchen counter — that's the ritual we protect. Brewed with care. Made for you." },
  faq: { title: "FAQ", body: "How fresh is your coffee? — Roasted weekly. How long does shipping take? — 2-4 business days across India. Can I customize a gift box? — Yes, write to us for bulk & corporate gifting." },
  shipping: { title: "Shipping Policy", body: "Free shipping on orders above ₹500. Standard delivery in 2-4 business days across India. Prepaid orders are dispatched within 24 hours." },
  returns: { title: "Return Policy", body: "Because our products are consumables, we accept returns only for damaged or incorrect items. Please write to us within 48 hours of delivery with photos and we'll make it right." },
  privacy: { title: "Privacy Policy", body: "We collect only what we need to fulfil your orders and improve your experience. We never sell your data. Full policy available on request." },
  terms: { title: "Terms & Conditions", body: "By purchasing from DailyBeans, you agree to our terms including pricing, delivery, and returns. Full T&Cs available on request." },
};

export default function StaticPage({ page }) {
  if (page === "contact") return <ContactPage/>;
  const c = STATIC[page] || { title: "Page", body: "" };
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-16">
      <h1 className="font-serif-display text-5xl mb-6">{c.title}</h1>
      <p className="text-stone-600 leading-relaxed whitespace-pre-line">{c.body}</p>
    </div>
  );
}

function ContactPage() {
  const [c, setC] = useState(null);
  useEffect(() => {
    api.get("/company").then(r => setC(r.data)).catch(() => setC({
      name: "DailyBeans Foods & Beverages Pvt Ltd",
      address_lines: ["#05, Balaji Serenity, Andhapura", "Electronics City 2, Bengaluru 560100"],
      gstin: "29AANCD0102E1Z3",
      email: "admin@mydailybean.com", phone: "+91-80-4000-0000",
    }));
  }, []);
  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-16" data-testid="contact-page">
      <div className="uppercase text-[10px] tracking-[0.3em] text-[#D4AF37] font-outfit font-semibold mb-2">Get in touch</div>
      <h1 className="font-serif-display text-5xl mb-4">Contact us</h1>
      <p className="text-stone-600 leading-relaxed max-w-2xl">
        We love hearing from fellow coffee people — questions, wholesale enquiries, feedback, or just to say hi.
      </p>

      <div className="grid md:grid-cols-2 gap-6 mt-10">
        <a href={`mailto:${c?.email || "admin@mydailybean.com"}`} data-testid="contact-email"
           className="group bg-white/80 backdrop-blur border border-stone-200 rounded-2xl p-6 hover:border-[#D4AF37]/60 hover:shadow-lg transition-all">
          <div className="w-11 h-11 rounded-xl bg-[#FEF3E2] flex items-center justify-center mb-3"><Mail className="w-5 h-5 text-[#D4AF37]"/></div>
          <div className="uppercase text-[10px] tracking-widest text-stone-500 font-semibold mb-1">Email us</div>
          <div className="font-outfit font-semibold text-[#3B2F2F] break-all">{c?.email || "—"}</div>
          <div className="text-xs text-stone-500 mt-1">Replies within 24 hours, Mon-Sat.</div>
        </a>
        <a href={`tel:${(c?.phone || "").replace(/\s|-/g,"")}`} data-testid="contact-phone"
           className="group bg-white/80 backdrop-blur border border-stone-200 rounded-2xl p-6 hover:border-[#D4AF37]/60 hover:shadow-lg transition-all">
          <div className="w-11 h-11 rounded-xl bg-[#E8EDDF] flex items-center justify-center mb-3"><Phone className="w-5 h-5 text-[#2E4A35]"/></div>
          <div className="uppercase text-[10px] tracking-widest text-stone-500 font-semibold mb-1">Call us</div>
          <div className="font-outfit font-semibold text-[#3B2F2F]">{c?.phone || "—"}</div>
          <div className="text-xs text-stone-500 mt-1">Mon-Sat, 9am-6pm IST.</div>
        </a>
      </div>

      <div className="mt-8 bg-white/80 backdrop-blur border border-stone-200 rounded-2xl p-6 md:p-8" data-testid="contact-company">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-[#F5F0E6] flex items-center justify-center shrink-0"><Building2 className="w-5 h-5 text-[#3B2F2F]"/></div>
          <div className="flex-1">
            <div className="uppercase text-[10px] tracking-widest text-stone-500 font-semibold mb-1">Registered office</div>
            <div className="font-outfit font-bold text-lg text-[#3B2F2F]" data-testid="contact-company-name">{c?.name || "—"}</div>
            <div className="text-stone-600 mt-2 flex items-start gap-2 text-sm">
              <MapPin className="w-4 h-4 text-stone-400 shrink-0 mt-0.5"/>
              <div>{(c?.address_lines || []).map((l,i)=><div key={i}>{l}</div>)}</div>
            </div>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <div><span className="uppercase text-[10px] tracking-widest text-stone-500 mr-2">GSTIN</span><span className="font-mono text-[#3B2F2F]" data-testid="contact-gstin">{c?.gstin || "—"}</span></div>
            </div>
          </div>
        </div>
      </div>

      <p className="text-xs text-stone-400 mt-6 text-center">
        For order-related queries, please include your order number (starting with DB) so we can help you faster.
      </p>
    </div>
  );
}
